import { boxHitsOtherDisk, boxOverlap, inflateBox } from '../../../runNeuralAnimationFrame/labelSlots';
import {
  TAG_SLOTS,
  closerTitleSeat,
  clearTitleSeatWant,
  emptiestGapAngle,
  hullOutwardSeats,
  labelOffView,
  labelOverlap,
  otherClusterPull,
  outwardAngle,
  pointInConvexHull,
  pushAlongNormal,
  TITLE_SEAT_LOCK,
  titleSeatSwitchReady,
  towardNeighborCost,
  titleOccluderWeight,
  wireOverlap,
  wrapAngle,
} from '../tagLayout';
import { applyTagLocal, rimAt } from './geometry';
import { tagPixelBox } from './project';
import { ensureTitleOnView, holdTitleInView } from './titleView';
import { ndc, NODE_CORE_RADIUS, tagWorld, TITLE_PUSH, TITLE_RIMS } from './vectors';

export function huntOneTitle(bunch, ctx) {
  const {
    camera, group, bunches, mid, outer, names, titleBoxes,
    nodeScreens, nodePixels, wireScreens, wirePad,
    cw, ch, padX, padY, viewPad, topInset, leftInset,
  } = ctx;
  const tag = bunch.userData.tag;
  const lwPx = tag.element.offsetWidth || 120;
  const lhPx = tag.element.offsetHeight || 16;
  const lw = Math.min(0.42, lwPx / cw * 2) + padX;
  const lh = (lhPx / ch * 2) + padY;
  const isOuter = outer.has(bunch);
  const outward = outwardAngle(bunch, mid);
  const home = emptiestGapAngle(bunch, bunches);
  const prev = bunch.userData.tagAngle ?? home;
  const prevRim = bunch.userData.tagRimK ?? 0.56;
  const ownKey = bunch.userData.clusterKey;
  const hullLocal = bunch.userData.titleHull || [];
  const scoreWorld = (wx, wy, rimK, angle) => {
    if (pointInConvexHull(wx - bunch.position.x, wy - bunch.position.y, hullLocal)) {
      return { score: Infinity, box: null, touch: 8 };
    }
    const fromHome = Math.abs(wrapAngle(angle - home));
    tagWorld.set(wx, wy, bunch.userData.tagZ ?? 0.45);
    ndc.copy(tagWorld).project(camera);
    if (ndc.z < -1 || ndc.z > 1) return { score: Infinity, box: null, touch: 8 };
    const box = tagPixelBox(ndc, lwPx, lhPx, cw, ch);
    const diskHit = boxHitsOtherDisk(box, nodePixels, null, 8);
    let nodesHit = diskHit ? 1 : 0;
    if (!diskHit) {
      for (let n = 0; n < nodeScreens.length; n += 1) {
        nodesHit += labelOverlap(ndc.x, ndc.y, lw, lh, nodeScreens[n]);
      }
    }
    let wiresHit = 0;
    for (let w = 0; w < wireScreens.length; w += 1) {
      wiresHit += wireOverlap(ndc.x, ndc.y, lw, lh, wireScreens[w], wirePad);
      if (wiresHit > 3) break;
    }
    let titlesHit = 0;
    titleBoxes.forEach((otherBox, other) => {
      if (other === bunch) return;
      titlesHit += boxOverlap(box, inflateBox(otherBox));
    });
    let namesHit = 0;
    for (let i = 0; i < names.length; i += 1) {
      namesHit += boxOverlap(box, inflateBox(names[i], 12))
        * titleOccluderWeight(names[i].clusterKey, ownKey, 1);
    }
    const offView = labelOffView(ndc.x, ndc.y, lw, lh, topInset, leftInset);
    const turn = Math.abs(wrapAngle(angle - prev));
    const otherPull = otherClusterPull(group, bunch, wx, wy);
    const outside = isOuter ? 1 - Math.cos(wrapAngle(angle - outward)) : 0;
    const inward = towardNeighborCost(bunch, bunches, angle);
    const cover = nodesHit > 0.002 ? 1 : 0;
    const nameCover = namesHit > 0.02 ? 1 : 0;
    const touch = cover + titlesHit + namesHit;
    const score = (cover ? 2400 : 0) + (nameCover ? 900 : 0)
      + nodesHit * 80 + wiresHit * 28 + titlesHit * 140 + namesHit * 180 + offView * 80
      + (rimK - 0.52) * 22
      + fromHome * 1.2
      + outside * 0.8
      + inward * 4
      + otherPull * 4
      + turn * 1.6;
    return { score, box, namesHit, titlesHit, rimK, touch, cover, nameCover, wx, wy, angle };
  };
  const scoreSeat = (angle, rimK) => {
    const local = rimAt(bunch, angle, rimK);
    return scoreWorld(bunch.position.x + local.x, bunch.position.y + local.y, rimK, angle);
  };
  let bestAngle = prev;
  let bestRim = prevRim;
  let best = scoreSeat(prev, prevRim);
  const stuckOnLabel = (best.namesHit || 0) + (best.titlesHit || 0) > 0.02;
  const hullWorld = hullLocal.map((p) => ({
    x: bunch.position.x + p.x,
    y: bunch.position.y + p.y,
  }));
  const baseGap = (bunch.userData.titleHullR ?? NODE_CORE_RADIUS) + 0.48;
  const consider = (next, angle, rimK) => {
    if (!closerTitleSeat(best, next, stuckOnLabel ? 10 : 5)) return;
    best = next;
    bestAngle = angle;
    bestRim = rimK;
  };
  if (hullWorld.length) {
    const dirs = hullOutwardSeats(hullWorld, 1);
    dirs.forEach((dir) => {
      const ox = dir.x - Math.cos(dir.angle);
      const oy = dir.y - Math.sin(dir.angle);
      const nx = Math.cos(dir.angle);
      const ny = Math.sin(dir.angle);
      const gaps = TITLE_PUSH.map((extra) => baseGap + extra);
      const pushed = pushAlongNormal(ox, oy, nx, ny, gaps, (x, y) => {
        const trial = scoreWorld(x, y, (x - ox) / Math.max(baseGap, 0.01), dir.angle);
        return (trial.touch ?? 0) > 0.002;
      });
      if (!pushed) return;
      const rimK = Math.max(0.52, (pushed.gap - baseGap) / 1.9 + 0.52);
      consider(scoreWorld(pushed.x, pushed.y, rimK, dir.angle), dir.angle, rimK);
    });
  } else {
    TITLE_RIMS.forEach((rimK) => {
      for (let i = 0; i < TAG_SLOTS; i += 1) {
        const angle = (i / TAG_SLOTS) * Math.PI * 2;
        consider(scoreSeat(angle, rimK), angle, rimK);
      }
    });
  }
  const lastScore = bunch.userData.tagLocalX != null
    ? scoreWorld(
      bunch.position.x + bunch.userData.tagLocalX,
      bunch.position.y + bunch.userData.tagLocalY,
      prevRim,
      prev,
    )
    : null;
  const keepLast = lastScore && (lastScore.touch ?? 0) <= 0.002 && Number.isFinite(lastScore.score);
  const lastOnName = (lastScore?.namesHit || 0) > 0.02;
  const lastOnDisk = (lastScore?.cover || 0) > 0;
  const lastLocal = bunch.userData.tagLocalX != null
    ? {
      x: bunch.userData.tagLocalX,
      y: bunch.userData.tagLocalY,
      z: bunch.userData.tagZ ?? 0.45,
    }
    : null;
  const lock = bunch.userData.titleSeatLock ?? 0;
  const nextLocal = best.wx != null
    ? { x: best.wx - bunch.position.x, y: best.wy - bunch.position.y, z: bunch.userData.tagZ ?? 0.45 }
    : rimAt(bunch, bestAngle, bestRim);
  let local;
  let follow = 1;
  const worseDisk = lastLocal && (best.cover || 0) > (lastScore?.cover || 0);
  if (keepLast && lastLocal) {
    clearTitleSeatWant(bunch.userData);
    if (lock > 0) bunch.userData.titleSeatLock = lock - 1;
    local = lastLocal;
  } else if (worseDisk) {
    clearTitleSeatWant(bunch.userData);
    local = lastLocal;
  } else if (lastLocal && lock > 0 && !lastOnName && !lastOnDisk) {
    bunch.userData.titleSeatLock = lock - 1;
    local = lastLocal;
  } else if (lastOnName || lastOnDisk || !lastLocal || titleSeatSwitchReady(bunch.userData, nextLocal.x, nextLocal.y)) {
    clearTitleSeatWant(bunch.userData);
    bunch.userData.titleSeatLock = lastLocal ? TITLE_SEAT_LOCK : 0;
    bunch.userData.tagAngle = bestAngle;
    bunch.userData.tagRimK = bestRim;
    local = nextLocal;
    follow = lastLocal ? 0.6 : 1;
  } else {
    local = lastLocal;
  }
  applyTagLocal(bunch, tag, {
    x: tag.position.x + (local.x - tag.position.x) * follow,
    y: tag.position.y + (local.y - tag.position.y) * follow,
    z: local.z,
  });
  tag.getWorldPosition(tagWorld);
  ndc.copy(tagWorld).project(camera);
  const chosenOff = labelOffView(ndc.x, ndc.y, lw, lh, topInset, leftInset);
  if (chosenOff > 0.02) {
    if (holdTitleInView(camera, bunch, tag, lwPx, lhPx, cw, ch, viewPad, titleBoxes, topInset, leftInset)) {
      ensureTitleOnView(camera, bunch, tag, lwPx, lhPx, cw, ch, viewPad, titleBoxes, topInset, leftInset);
      return;
    }
    ensureTitleOnView(camera, bunch, tag, lwPx, lhPx, cw, ch, viewPad, titleBoxes, topInset, leftInset);
    return;
  }
  titleBoxes.set(bunch, tagPixelBox(ndc, lwPx, lhPx, cw, ch));
}
